/**
 * <localization-modal> – two-step country → language selector web component.
 *
 * Flow:
 *  1. Trigger click → opens country modal.
 *  2. Country click → stores iso_code in memory, closes country modal,
 *     reads the languages JSON pre-rendered by Liquid inside the component,
 *     then opens the language modal (no extra network request).
 *  3. Language click → writes both country_code + locale_code into the
 *     unified hidden form and submits it (one page reload at the very end).
 */

const LANGUAGE_FLAG_MAP = {
  de: "de",
  fr: "fr",
  nl: "nl",
  en: "gb",
  pl: "pl",
  da: "dk",
  sv: "se",
  nb: "no",
  fi: "fi",
  cs: "cz",
  sk: "sk",
  hu: "hu",
  pt: "pt",
  es: "es",
  it: "it",
  ro: "ro",
};

class LocalizationModal extends HTMLElement {
  connectedCallback() {
    // Wait until modal-dialog is defined so .open() is available
    customElements.whenDefined("modal-dialog").then(() => this._init());
  }

  _init() {
    // Read languages pre-rendered by Liquid
    const jsonEl = this.querySelector("script[data-languages]");
    this._languages = jsonEl ? JSON.parse(jsonEl.textContent) : [];

    this._trigger = this.querySelector("[data-trigger]");
    this._countryModal = this.querySelector("[data-country-modal]");
    this._languageModal = this.querySelector("[data-language-modal]");
    this._languageGrid = this.querySelector("[data-language-grid]");
    this._countryInput = this.querySelector("[data-country-input]");
    this._localeInput = this.querySelector("[data-locale-input]");
    this._form = this.querySelector("form[id]");

    // Trigger → open country modal
    this._trigger?.addEventListener("click", () => {
      this._trigger.setAttribute("aria-expanded", "true");
      this._countryModal.open(this._trigger);
      this._countryModal.addEventListener(
        "close",
        () => this._trigger.setAttribute("aria-expanded", "false"),
        { once: true },
      );
    });

    // Country button clicks
    this.querySelectorAll("[data-country]").forEach((btn) => {
      btn.addEventListener("click", () => this._selectCountry(btn));
    });
  }

  async _selectCountry(btn) {
    const code = btn.dataset.country;

    // Store selection in the hidden input (no form submit yet)
    this._countryInput.value = code;

    // Close country modal
    this._countryModal.close();

    const languages = this._languages;

    // If only one language available, skip language step and submit directly
    if (languages.length <= 1) {
      if (languages[0]) this._localeInput.value = languages[0].iso_code;
      this._form.submit();
      return;
    }

    this._renderLanguageOptions(languages);
    this._languageModal.open(null);
  }

  _renderLanguageOptions(languages) {
    this._languageGrid.innerHTML = languages
      .map((lang) => {
        const flagCode = LANGUAGE_FLAG_MAP[lang.iso_code] ?? lang.iso_code;
        return `
          <button
            type="button"
            class="localization-modal__item"
            data-locale="${lang.iso_code}"
            role="option"
            aria-selected="false"
          >
            <img
              src="https://flagcdn.com/w80/${flagCode}.png"
              width="80"
              height="60"
              alt="${lang.name}"
              class="localization-modal__item-flag"
              onerror="this.classList.add('is-hidden')"
            >
            <span class="localization-modal__item-name">${lang.name}</span>
          </button>`;
      })
      .join("");

    this._languageGrid.querySelectorAll("[data-locale]").forEach((btn) => {
      btn.addEventListener("click", () => {
        this._localeInput.value = btn.dataset.locale;
        this._languageModal.close();
        this._form.submit();
      });
    });
  }
}

customElements.define("localization-modal", LocalizationModal);
